// src/components/Forge/Watchlist/ideaCopy.js
//
// Pilot P1a — the Forge "Idea" panel's words. The lifecycle SENTENCES are
// table C of docs/specs/MODE_TRUTH_LANGUAGE_TABLES_V1.md, VERBATIM (blessed by
// the founder 24 Sep 2026 — "the only source player-facing strings ship
// from"); ideaCopy.test.js reads that table and pins every line byte for byte.
// Placeholders in [brackets] are filled from the record, never invented: when
// a placeholder has no recorded value the line is not rendered at all
// (fillLine → null), and the panel shows the typed status chip alone.
//
// The labels below (status, horizon, actions) are UI labels, not table
// sentences; none uses table E's forbidden vocabulary (ideaCopy.test.js scans).

import { HORIZON_WINDOW_SESSIONS, DUE_DEPLOY_LINE, REVIEW_HONEST_LINE, STATE_REASONS } from '../../../constants/hypothesisRecords';
import { RESEARCH_STAGES } from '../../../constants/researchRecords';

/**
 * Table C, verbatim (V1 rows, the two [LIST] rows of table C V1.1 — founder
 * ruling B3, 8 Oct 2026 — and the rows of table C V1.2 — founder rulings
 * E1–E3, 16 Oct 2026, Pilot P1c).
 */
export const LIFECYCLE_LINES = Object.freeze({
  reviewDueHorizon: "Your [SYM] idea reached its time-frame ([window]). Nothing was sold and nothing was deleted — it's flagged for your review. Reaffirm it to make a fresh version, or retire it.",
  reviewDueBattleEnded: "The battle ended with your [SYM] idea still open-ended. It's flagged for review — reaffirm or retire when you're ready.",
  invalidated: "Your [SYM] idea hit its invalidation: [typed condition]. That's recorded on the idea itself — what happens next is your call.",
  reaffirmed: "Reaffirmed — that's a fresh version of the same idea with a new clock. The old one stays in the record exactly as it was.",
  // Pilot P1b's deploy admission (a due version cannot deploy): ONE string, shared with the
  // deploy endpoint's refusal (src/constants/hypothesisRecords.js DUE_DEPLOY_LINE).
  dueDeploy: DUE_DEPLOY_LINE,
  // Table C V1.1 (founder ruling B3): the review lines for an idea that names more than one symbol.
  reviewDueHorizonList: "Your [LIST] idea reached its time-frame ([window]). Nothing was sold and nothing was deleted — it's flagged for your review. Reaffirm it to make a fresh version, or retire it.",
  reviewDueBattleEndedList: "The battle ended with your [LIST] idea still open-ended. It's flagged for review — reaffirm or retire when you're ready.",
  // Table C V1.2 (ruling E1): the deploy station's screen before a deploy whose idea is due — its title…
  dueDeployTitle: 'Your [SYM] idea reached its time-frame.',
  dueDeployTitleList: 'Your [LIST] idea reached its time-frame.',
  // …and, after a deploy that carried nothing because the idea is due, the ceremony's success screen.
  deployRanWithout: "This battle is running without your idea — it's still waiting for your review.",
  // Table C V1.2 (ruling E2): the review card's honest line (the server fills [AGENT]; one shared string).
  reviewHonest: REVIEW_HONEST_LINE,
  // Table C V1.2 (ruling E3): a version whose only carrying battle never ran, unwound to Ready.
  deployVoided: "That battle never ran, so your idea is back to Ready. Its clock hasn't started.",
});

/** Table C V1.2 (ruling E1): the deploy station screen's three buttons, in this order — no fourth option. */
export const DUE_DEPLOY_ACTIONS = Object.freeze([
  Object.freeze({ id: 'reaffirm', label: 'Reaffirm and deploy' }),
  Object.freeze({ id: 'retire', label: 'Retire and deploy' }),
  Object.freeze({ id: 'without', label: 'Deploy without it' }),
]);

/** Table C V1.2 (ruling E2): the review card's labels. */
export const REVIEW_CARD_LABELS = Object.freeze({
  difference: 'Difference',
  points: 'pts',
  names: 'Each name',
  noData: 'No data',
  countedOf: '[n] of [N]',
  battles: 'Battles',
  voided: 'voided',
});

/** The list figure's "[n] of [N]" (table C V1.2), or null when it counts every name (or the counts are not numbers). */
export function countedOfText(counted, total) {
  if (!Number.isInteger(counted) || !Number.isInteger(total) || counted < 0 || counted >= total) return null;
  return REVIEW_CARD_LABELS.countedOf.replace('[n]', String(counted)).replace('[N]', String(total));
}

/**
 * Fill a table-C line's placeholders. Returns null when any placeholder the
 * line uses has no value — a sentence is never rendered with an invented or
 * blank slot.
 *
 * @param {string} line  a LIFECYCLE_LINES value
 * @param {{ sym?: string|null, list?: string|null, window?: string|null, condition?: string|null, agent?: string|null }} values
 *   `agent` (table C V1.2's [AGENT]) is the name the deploying battle froze, never the live agent's.
 */
export function fillLine(line, { sym = null, list = null, window = null, condition = null, agent = null } = {}) {
  const slots = { '[SYM]': sym, '[LIST]': list, '[window]': window, '[typed condition]': condition, '[AGENT]': agent };
  let out = line;
  for (const [slot, value] of Object.entries(slots)) {
    if (!out.includes(slot)) continue;
    if (typeof value !== 'string' || value.trim() === '') return null;
    out = out.split(slot).join(value);
  }
  return out;
}

const distinctSymbols = (list) => [...new Set((Array.isArray(list) ? list : []).filter((s) => typeof s === 'string' && s.trim() !== ''))];

/**
 * [SYM] — founder ruling B3 (table C V1.1, 8 Oct 2026): filled ONLY when the
 * idea names exactly one symbol — its own typed conditions when it has any,
 * else the deploying battle's FROZEN snapshot (`frozen`, the server's
 * `deployedLists[version]`) when that froze exactly one ticker. Otherwise
 * null, and the line takes its [LIST] variant (ideaListOf). Never the parent
 * list's live tickers: they are mutable, not part of the version, and in the
 * editor they are the live, unsaved input (spec §2.6 "no reader resolves the
 * current saved list as historical meaning"; BUILD_RULES §9; P1a reviews
 * L1-2 / L4-6).
 *
 * @param {object} version  the version record
 * @param {{ name: string, tickers: string[] }|null} [frozen]  the frozen list it was deployed in
 */
export function ideaSymbolOf(version, frozen = null) {
  const own = distinctSymbols([...(version?.activation || []), ...(version?.invalidation || [])].map((c) => c?.symbol));
  if (own.length > 0) return own.length === 1 ? own[0] : null;
  const froze = distinctSymbols(frozen?.tickers);
  return froze.length === 1 ? froze[0] : null;
}

/**
 * [LIST] — founder ruling B3: the FROZEN list name from the deploying battle's
 * snapshot (via the version's lastDeployedBattleId, resolved by the server),
 * never the live list's name. Null when no frozen list is known.
 */
export function ideaListOf(frozen = null) {
  return typeof frozen?.name === 'string' && frozen.name.trim() !== '' ? frozen.name : null;
}

/**
 * The table-C sentence for a version's state, or null when none applies or a
 * placeholder has no recorded value. `invalidated` renders nothing in P1a:
 * [typed condition] is the MET condition on the record, which P3/P4 define —
 * a raw reason code is never put into a blessed sentence (review L1-6).
 *
 * Pilot P1b (founder ruling B3): [SYM] when the idea names exactly one symbol
 * (ideaSymbolOf), else the [LIST] row with the frozen list's name
 * (`frozen` = the server's deployedLists[version]); neither known → no line.
 * The battle-ended row is table C's `review_due (battle ended, unspecified)`:
 * it says "still open-ended", so it renders only for an `unspecified`
 * horizon — a version whose clock fell back to a battle-end review (ruling B5,
 * `reviewClockFault`) shows its status chip alone.
 */
export function lifecycleLineFor(version, frozen = null) {
  if (!version || version.status !== 'review_due') return null;
  const sym = ideaSymbolOf(version, frozen);
  const list = sym ? null : ideaListOf(frozen);
  if (version.stateReason === 'horizon_elapsed') {
    const window = windowTextOf(version.horizonEnum);
    if (sym) return fillLine(LIFECYCLE_LINES.reviewDueHorizon, { sym, window });
    return list ? fillLine(LIFECYCLE_LINES.reviewDueHorizonList, { list, window }) : null;
  }
  if (version.stateReason === 'battle_ended' && version.horizonEnum === 'unspecified') {
    if (sym) return fillLine(LIFECYCLE_LINES.reviewDueBattleEnded, { sym });
    return list ? fillLine(LIFECYCLE_LINES.reviewDueBattleEndedList, { list }) : null;
  }
  return null;
}

/**
 * Pilot P1c (founder ruling E1; table C V1.2): the deploy station screen's
 * title for a due version whose time-frame elapsed (`horizon_elapsed`) —
 * [SYM] when the idea names exactly one symbol (ideaSymbolOf), else the
 * FROZEN list's name (ideaListOf on the GET's `deployedLists[version]`), B3's
 * rule exactly. "Reached its time-frame" is a claim the record must hold
 * (review L4-1): an idea due because its battle ended shows table C's own
 * line for that state instead (lifecycleLineFor — the Forge's line, word for
 * word), and a version whose clock fell back to a battle-end review (B5)
 * shows none. No name known → null: the screen shows its three buttons with
 * no title, never an invented one.
 */
export function dueDeployTitleFor(version, frozen = null) {
  if (!version || version.status !== 'review_due') return null;
  if (version.stateReason !== 'horizon_elapsed') return lifecycleLineFor(version, frozen);
  const sym = ideaSymbolOf(version, frozen);
  if (sym) return fillLine(LIFECYCLE_LINES.dueDeployTitle, { sym });
  const list = ideaListOf(frozen);
  return list ? fillLine(LIFECYCLE_LINES.dueDeployTitleList, { list }) : null;
}

/**
 * Pilot P1c (founder ruling E3; table C V1.2): the line for a version the
 * review pass (or the next deploy) unwound to Ready because its only
 * carrying battle never ran —
 * read from the record's own state (status `ready`, reason `deploy_voided`),
 * never inferred. Null otherwise.
 */
export function deployVoidedLineFor(version) {
  return version?.status === 'ready' && version?.stateReason === STATE_REASONS.deployVoided ? LIFECYCLE_LINES.deployVoided : null;
}

export const HORIZON_LABELS = Object.freeze({
  intraday: 'Intraday', swing: 'Swing', positional: 'Positional', longterm: 'Long-term', unspecified: 'No time-frame',
});

/** [window] from the record's horizon: the companion §6 window, e.g. "Swing, 10 trading sessions". Null for unspecified. */
export function windowTextOf(horizonEnum) {
  const n = HORIZON_WINDOW_SESSIONS[horizonEnum];
  return Number.isInteger(n) ? `${HORIZON_LABELS[horizonEnum]}, ${n} trading sessions` : null;
}

export const HORIZON_SOURCE_LABELS = Object.freeze({
  parse: 'from your drop', theme_default: 'theme default', default: 'not set yet', player: 'your pick',
});

export const STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  researched: 'Researched',
  ready: 'Ready',
  waiting_for_evidence: 'Waiting for evidence',
  activated: 'Activated',
  invalidated: 'Invalidated',
  review_due: 'Review due',
  retired: 'Retired',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
});

export const ACTION_LABELS = Object.freeze({
  ready: 'Mark ready',
  wait: 'Waiting for evidence',
  reject: 'Reject',
  cancel: 'Cancel idea',
  retire: 'Retire',
  reaffirm: 'Reaffirm',
  mark_researched: 'Mark researched', // Pilot P2 / founder ruling D4
});

// ── Pilot P2 — the research line (the P2 build prompt's "Forge copy",
// approve-by-default). Every number is a stage count from the research
// record itself; a stage the record holds as null (its host has no such
// stage) is not shown at all — never as 0 (BUILD_RULES §9).

export const RESEARCH_COPY = Object.freeze({
  lead: 'Researched with your agent:',
  stillOpen: 'Research still open',
  endedEarly: 'Research ended early',
  playerMarked: 'You marked this researched.',
  none: 'No research recorded for this idea yet.',
});

/** Each stage's word (the line's pipeline order is RESEARCH_STAGES). */
export const STAGE_LABELS = Object.freeze({
  universeSize: 'screened',
  matchedPreLimit: 'matched',
  shortlisted: 'returned',
  // The analysis stages say what they are — the saved set, and the members the
  // session had ranking data for — never "investigated" (reviews R4-3 / R1-3).
  selectedForInvestigation: 'in the set',
  investigationsCompleted: 'with data',
  eligible: 'kept',
});
/** A dialogue's shortlist is the candidates the agent proposed. */
export const DIALOGUE_SHORTLIST_LABEL = 'candidates';
const DIALOGUE_ORIGINS = Object.freeze(['signaldrop', 'theme']);

/**
 * The stages each origin's line shows, in pipeline order — the copy's own
 * shapes: the screener's "screened · matched · returned · kept", the
 * dialogue's "candidates · kept" (its selection always equals what the save
 * kept, so the record keeps it and the line does not repeat it — reviews
 * R4-2 / R1-7), the analysis session's "in the set · with data". A stage the
 * record holds as null is never shown, not even here.
 */
export const LINE_STAGES = Object.freeze({
  signaldrop: Object.freeze(['shortlisted', 'eligible']),
  theme: Object.freeze(['shortlisted', 'eligible']),
  screener: Object.freeze(['universeSize', 'matchedPreLimit', 'shortlisted', 'eligible']),
  analysis: Object.freeze(['selectedForInvestigation', 'investigationsCompleted']),
  manual: Object.freeze([]),
});

const isCount = (v) => Number.isSafeInteger(v) && v >= 0;

/**
 * "Researched with your agent: 500 screened · 37 matched · 25 returned · 25 kept"
 * from one research summary — its origin's stages that the record holds, in
 * pipeline order. Null when there is nothing to say: a manual record (the
 * player wrote the list), a record no model turn completed (nothing was
 * researched WITH the agent — an analysis view opened and left), or a
 * screener record that recorded no screen.
 */
export function researchLineOf(summary) {
  const stages = summary?.stages || {};
  const shown = LINE_STAGES[summary?.origin] || [];
  if (!(isCount(summary?.completions) && summary.completions > 0)) return null;
  if (summary.origin === 'screener' && !(stages.universeSize > 0)) return null;
  const parts = RESEARCH_STAGES.filter((k) => shown.includes(k) && isCount(stages[k])).map((k) => {
    const label = k === 'shortlisted' && DIALOGUE_ORIGINS.includes(summary.origin) ? DIALOGUE_SHORTLIST_LABEL : STAGE_LABELS[k];
    return `${stages[k]} ${label}`;
  });
  return parts.length ? `${RESEARCH_COPY.lead} ${parts.join(' · ')}` : null;
}

/** The session state, said only when the research did not complete. */
export function researchStateOf(summary) {
  if (summary?.state === 'open') return RESEARCH_COPY.stillOpen;
  if (summary?.state === 'abandoned' || summary?.state === 'failed') return RESEARCH_COPY.endedEarly;
  return null;
}

export const RESEARCH_LINES_MAX = 2;

/**
 * The lines the panel shows for a list: the research the list itself came
 * from first, then — at most one — its newest analysis session that the agent
 * worked on. Only records whose subject IS this list (a record closed against
 * another list describes that list's save — review R4-4 / R1-1).
 */
export function researchLinesOf(research, { watchlistId = null } = {}) {
  const lines = (Array.isArray(research) ? research : [])
    .filter((s) => s && (watchlistId === null || s.watchlistId === watchlistId))
    .map((s) => ({ id: s.researchWorkId, origin: s.origin, line: researchLineOf(s), state: researchStateOf(s) }))
    .filter((r) => r.line);
  const own = lines.filter((r) => r.origin !== 'analysis');
  const analysis = lines.find((r) => r.origin === 'analysis');
  return [...own, ...(analysis ? [analysis] : [])].slice(0, RESEARCH_LINES_MAX)
    .map(({ id, line, state }) => ({ id, line, state }));
}

/**
 * "No research recorded for this idea yet." — said only when it is literally
 * true and about an idea that exists (review R4-7): the server answered with
 * the list's research (not merely an older answer without it), the list has
 * NO research record at all (a manual list has its own record), a current
 * version exists, and that version is not already marked researched by the
 * dialogue it came from.
 */
export function noResearchRecorded(research, current) {
  return Array.isArray(research) && research.length === 0 && !!current && current.stateReason !== 'dialogue_completed';
}

/** The panel's own prompts and notes (UI copy; no lifecycle claims). */
export const PANEL_COPY = Object.freeze({
  title: 'Idea',
  empty: 'No idea is recorded for this list yet.',
  writeFirst: 'Write the idea',
  saveNew: 'Save as a new version',
  editorHint: 'Editing never changes a saved version — it creates the next one.',
  draftHint: 'The new version starts as a draft.',
  statementPlaceholder: 'State the idea in a sentence or two',
  horizonPick: 'Time-frame',
  horizonKeep: 'Keep the current time-frame',
  // A first version inherits the list's default (a SignalDrop list's parsed
  // time-frame, else none) — the panel cannot see it, so it never names one (review L4-4).
  horizonDefault: 'Default for this list',
  missingEvidencePrompt: 'What evidence is missing?',
  confirm: 'Confirm',
  keep: 'Keep',
  closeConfirm: 'This closes the idea version for good.',
  save: 'Save',
  cancelEdit: 'Back',
  history: 'Versions',
  waitingOn: 'Waiting on',
  loadFailed: 'The idea record could not be loaded.',
  retry: 'Retry',
  moveFailed: 'That change did not go through. Try again.',
  saved: 'Saved',
  statusSince: 'Status since',
  firstDeployed: 'First deployed',
  reviewDue: 'Review due',
});

const ET_DATE = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });

/** A record timestamp as a market-calendar date ("Oct 7, 2026"); null for null. */
export function formatIdeaDate(iso) {
  if (typeof iso !== 'string' || !iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : ET_DATE.format(d);
}
